import { useEffect } from 'react';

import { Button } from '@mui/material';
import Container from '@mui/material/Container';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { getPackagingDamageDispatchById } from 'src/redux/slices/packaging-damage-dispatch';

import ErrorContent from 'src/components/error-content';
import { useSettingsContext } from 'src/components/settings';
import { LeftIcon } from 'src/components/carousel/arrow-icons';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { LoadingScreen } from '../../../components/loading-screen';
import { useAppDispatcher, useAppSelector } from '../../../redux/store';
import PackagingDamageDispatchDetails from '../packaging-damage-dispatch-details';

type Props = {
  id: string;
};

export default function PackagingDamageDispatchDetailView({ id }: Props) {
  const {
    damageDispatch: currentDamageDispatch,
    status,
    error,
  } = useAppSelector((state) => state.packagingDamageDispatch);

  const settings = useSettingsContext();
  const dispatch = useAppDispatcher();

  useEffect(() => {
    dispatch(getPackagingDamageDispatchById({ itemID: id }));
  }, [dispatch, id]);

  if (status === 'loading' || status === 'idle') {
    return <LoadingScreen />;
  }

  if (status === 'failure') {
    return (
      <ErrorContent
        filled
        title="Failed to load damage dispatch details"
        description={error}
        action={
          <Button
            component={RouterLink}
            href={paths.dashboard.packagingDamageDispatch.root}
            variant="contained"
            startIcon={<LeftIcon />}
          >
            Back to Damage Dispatch
          </Button>
        }
        sx={{
          py: 10,
        }}
      />
    );
  }

  if (status === 'success' && currentDamageDispatch) {
    return (
      <Container maxWidth={settings.themeStretch ? false : 'lg'}>
        <CustomBreadcrumbs
          heading="Damage Dispatch Details"
          links={[
            {
              name: 'Damage Dispatch',
              href: paths.dashboard.packagingDamageDispatch.root,
            },
            { name: 'Damage Dispatch Details' },
          ]}
          sx={{
            mb: { xs: 3, md: 5 },
          }}
        />

        <PackagingDamageDispatchDetails currentDamageDispatch={currentDamageDispatch} />
      </Container>
    );
  }

  return <span />;
}
